// Spiegelbild von backend/security/auth.py — dieselbe Regel-Logik im Frontend.
// Verhindert, dass Nutzer im UI Rollen sehen/wählen, die sie ohnehin nicht vergeben dürfen.

export const ROLE_HIERARCHY = {
  DEVELOPER: 6,
  WACHLEITUNG: 5,
  WACHLEITUNG_VERTRETUNG: 4,
  WACHABTEILUNGSLEITUNG: 3,
  ADMIN: 2,
  MITARBEITER: 1,
};

/**
 * Prüft, ob ein Manager (currentUserRolle) eine bestimmte Ziel-Rolle vergeben darf.
 * Regeln:
 *   DEVELOPER              → alle
 *   WACHLEITUNG            → alle außer DEVELOPER
 *   WACHLEITUNG_VERTRETUNG → alle Rollen unterhalb der eigenen
 *   WACHABTEILUNGSLEITUNG  → ADMIN + MITARBEITER
 *   ADMIN                  → nur MITARBEITER
 *   MITARBEITER            → niemanden
 */
export function canManageRole(managerRolle, targetRolle) {
  if (!managerRolle || !targetRolle) return false;
  if (managerRolle === 'DEVELOPER') return true;
  if (managerRolle === 'WACHLEITUNG') return targetRolle !== 'DEVELOPER';
  if (managerRolle === 'WACHLEITUNG_VERTRETUNG') {
    return targetRolle !== 'DEVELOPER'
      && targetRolle !== 'WACHLEITUNG'
      && targetRolle !== 'WACHLEITUNG_VERTRETUNG';
  }
  if (managerRolle === 'WACHABTEILUNGSLEITUNG') {
    return targetRolle === 'ADMIN' || targetRolle === 'MITARBEITER';
  }
  if (managerRolle === 'ADMIN') return targetRolle === 'MITARBEITER';
  return false;
}

/**
 * Filtert eine Liste von Rollen-Optionen ({ value, label }) auf die für den
 * angemeldeten User erlaubten Ziel-Rollen. Wenn eine `currentRolle` übergeben
 * wird (Rolle des bearbeiteten Users), bleibt diese als disabled sichtbar in
 * der Liste, damit man den bestehenden Wert nicht verliert, aber nicht ändern
 * kann.
 */
export function filterRoleOptions(options, managerRolle, currentTargetRolle = null) {
  return options
    .map((opt) => {
      const allowed = canManageRole(managerRolle, opt.value);
      const isCurrent = opt.value === currentTargetRolle;
      return { ...opt, disabled: !allowed && !isCurrent, hidden: !allowed && !isCurrent };
    })
    .filter((opt) => !opt.hidden);
}

/**
 * Nur ab WACHLEITUNG_VERTRETUNG aufwärts darf die Wachabteilung eines Users
 * geändert werden. ADMIN und WACHABTEILUNGSLEITUNG dürfen das nicht.
 */
export function canChangeWachabteilung(managerRolle) {
  return (ROLE_HIERARCHY[managerRolle] || 0) >= ROLE_HIERARCHY.WACHLEITUNG_VERTRETUNG;
}

const USER_MANAGEMENT_ACTIONS = new Set(['create', 'update', 'delete']);
const GLOBAL_USER_MANAGEMENT_ROLES = new Set([
  'DEVELOPER',
  'WACHLEITUNG',
  'WACHLEITUNG_VERTRETUNG',
]);

export function canManageUserAction(
  actor,
  targetRole,
  targetDepartmentId,
  effectivePermissions = [],
  action,
) {
  if (!actor || !USER_MANAGEMENT_ACTIONS.has(action)) return false;
  const permissions = new Set(effectivePermissions || []);
  const hasPermission = actor.rolle === 'DEVELOPER'
    || permissions.has(`users.${action}`);
  if (!hasPermission || !canManageRole(actor.rolle, targetRole)) return false;
  if (GLOBAL_USER_MANAGEMENT_ROLES.has(actor.rolle)) return true;
  if (actor.abteilung_id && actor.abteilung_id === targetDepartmentId) return true;
  return permissions.has(`users.${action}_all_abteilungen`);
}

export function canManageAllDepartments(actor, effectivePermissions = [], action) {
  if (!actor || !USER_MANAGEMENT_ACTIONS.has(action)) return false;
  if (GLOBAL_USER_MANAGEMENT_ROLES.has(actor.rolle)) return true;
  return (effectivePermissions || []).includes(`users.${action}_all_abteilungen`);
}
